<template>
  <div class="dependencies-manager pt-2 px-2 pb-6">
    <v-menu open-on-hover location="start" transition="slide-x-reverse-transition" content-class="rounded">
      <template #activator="{ props }">
        <div class="small text-medium-emphasis text-center line-height-normal px-2 mb-4" style="cursor: default" v-bind="props">
          <v-icon size="small" class="mr-1" style="top: -1px">far fa-info-circle</v-icon>{{ $t('DEPENDENCIES.ADD_DRAG_DROP') }}
        </div>
      </template>
      <v-card class="pa-4" elevation="2">
        <img src="./dependencies-drag-drop.svg" alt="Dependencies drag and drop" style="width: 240px">
      </v-card>
    </v-menu>
    <v-row no-gutters class="flex-nowrap milestones-inline">
      <v-col cols="4" style="position: relative; flex-shrink: 1">
        <dependencies-manager-targets-col :dependencies="validDependencies.predecessors"
                                          @set-main-el="setMainEl" @delete="deletePredecessor" @update="updateDependency">
        </dependencies-manager-targets-col>

        <div class="text-center">
          <v-btn icon color="successgreen" :active="showAddDependency && ! isSuccessor" size="x-small" variant="outlined" @click="showAddDependency = true; isSuccessor = false">
            <v-icon size="large" color="successgreen">far fa-plus</v-icon>
          </v-btn>
        </div>
      </v-col>
      <v-col class="px-1">
        <svg class="dependencies-svg" style="position: relative; top: -3px; left: 0; width: 24px; height: 8px; z-index: 105">
          <circle cx="4" cy="4" r="4" class="dependency-startpoint" />
          <path d="M 4 4 H 21" class="dependency-path" />
          <path d="M 18 0 v 8 l 6 -4 Z" class="dependency-endarrow" />
        </svg>
      </v-col>
      <v-col cols="4" class="mb-2" style="max-height:100px; overflow:hidden; flex-shrink: 1">
        <planning-element-view :el="displayedEl" disable-mini-element disable-styling>
        </planning-element-view>
      </v-col>
      <v-col class="px-1">
        <svg class="dependencies-svg" style="position: relative; top: -3px; left: 0; width: 24px; height: 8px; z-index: 105">
          <circle cx="4" cy="4" r="4" class="dependency-startpoint" />
          <path d="M 4 4 H 21" class="dependency-path" />
          <path d="M 18 0 v 8 l 6 -4 Z" class="dependency-endarrow" />
        </svg>
      </v-col>
      <v-col cols="4" style="position: relative; flex-shrink: 1">
        <dependencies-manager-targets-col :dependencies="validDependencies.successors" successors
                                          @set-main-el="setMainEl" @delete="deleteSuccessor" @update="updateDependency">
        </dependencies-manager-targets-col>

        <div class="text-center">
          <v-btn icon color="successgreen" :active="showAddDependency && isSuccessor" size="x-small" variant="outlined" @click="showAddDependency = true; isSuccessor = true">
            <v-icon size="large">far fa-plus</v-icon>
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <v-expand-transition>
      <div v-show="showAddDependency" class="mt-2 pr-2">
        <div class="d-flex align-center mb-1">
          {{ $t('DEPENDENCIES.ADD_NEW') }}
          <v-btn-icon size="x-small" @click="showAddDependency = false" class="ml-auto">
            <v-icon>far fa-times</v-icon>
          </v-btn-icon>
        </div>
        <select-element v-model="newTarget" :items="dependencyCandidates"></select-element>
        <div v-if="canEditDependencyType" class="mt-2">
          <btn-toggle-field v-model="newTargetType" :items="dependencyTypeItems" mandatory="force"
                            :btn-props="{ size: 'default', class: 'px-2' }">
          </btn-toggle-field>
        </div>
        <div class="mt-2 d-flex align-center">
          <btn-toggle-field v-model="isSuccessor" :items="[{ title: $t('DEPENDENCIES.PREDECESSOR'), value: false }, { title: $t('DEPENDENCIES.SUCCESSOR'), value: true }]" mandatory="force"
                            :btn-props="{ size: 'default', class: 'px-2' }">
          </btn-toggle-field>
          <span class="ml-auto">{{ $t('DEPENDENCIES.DELAY') }} :</span>
          <v-text-field :model-value="newTargetDelay" type="number" class="mx-2" style="flex: 0 0 70px"
                        @update:model-value="newTargetDelay = parseInt($event) || 0">
          </v-text-field>
          <span>{{ $t('GLOBAL.DAYS', newTargetDelay) }}</span>
        </div>
        <v-btn :disabled="! newTarget" color="primary" class="mt-2" rounded size="large" @click="addDependency()">
          {{ $t(newTargetIsAlreadyDependency ? 'DEPENDENCIES.MODIFY' : 'DEPENDENCIES.ADD') }}
        </v-btn>
        <div class="mt-2 small text-medium-emphasis line-height-normal">{{ $t('DEPENDENCIES.AUTOMATIC_UPDATE') }}</div>
      </div>
    </v-expand-transition>

    <div class="mt-4"><b class="text-primary">{{ $t('GLOBAL.OPTIONS') }}</b></div>
    <div class="ml-6">
      <v-switch v-model="planning.config.displayDependencies" :label="$t('DEPENDENCIES.DISPLAY_ON_PLANNING')"></v-switch>
      <v-switch v-model="planning.config.displayCriticalPath" :disabled="! planning.config.displayDependencies" :label="$t('DEPENDENCIES.DISPLAY_CRITICAL_PATH')"></v-switch>
      <premium-section feature="auto-optimize-dependencies">
        <v-switch v-model="planning.config.optimizeDependencies" :label="$t('DEPENDENCIES.OPTIMIZE_DEPENDENCIES')"></v-switch>
        <template #noaccess>
          <div class="d-flex align-center position-relative">
            <v-switch :model-value="false" disabled :label="$t('DEPENDENCIES.OPTIMIZE_DEPENDENCIES')"></v-switch>
            <premium-section icon feature="auto-optimize-dependencies" style="position: absolute"></premium-section>
          </div>
        </template>
      </premium-section>
    </div>

    <div class="mt-4 mb-1"><b class="text-primary">{{ $t('DEPENDENCIES.OPTIMIZATION') }}</b></div>
    <div class="small text-medium-emphasis mb-.5">{{ $t('DEPENDENCIES.REDUCE_ELEMENT_SUCCESSORS') }}</div>
    <v-btn :disabled="! elHasDependencies" variant="outlined" color="primary" rounded @click="reduceElDependenciesToMinimum()">
      {{ $t('DEPENDENCIES.OPTIMIZE_SUCCESSORS') }}
    </v-btn>
    <div class="mt-2 small text-medium-emphasis mb-.5">{{ $t('DEPENDENCIES.REDUCE_ELEMENT_PREDECESSORS') }}</div>
    <v-btn :disabled="! elHasDependencies" variant="outlined" color="primary" rounded @click="reduceElDependenciesToMinimum('predecessors')">
      {{ $t('DEPENDENCIES.OPTIMIZE_PREDECESSORS') }}
    </v-btn>
  </div>
</template>

<style lang="scss">
  .dependencies-manager {
    .element {
      position: relative;
      cursor: default;
    }
  }
</style>

<script>
  import useDependenciesStore from '@/stores/dependencies';
  import useUsersAccessRightStore from '@/stores/users/accessRight';
  import usePlanningElementsStore from '@/stores/planning/elements';
  import DependenciesSrv from '@/components/Dependencies/DependenciesSrv';
  import { openReduceElDependenciesDialog } from '@/components/Dependencies/dependencyDialogs';
  import { getAnchorTime, getDependencyAnchors } from './dependencyHelpers';
  import DependenciesManagerTargetsCol from './DependenciesManagerTargetsCol.vue';
  import PlanningElementView from '@/components/Planning/PlanningElementView.vue';
  import SelectElement from '@/components/Reusables/SelectElement.vue';

  export default {
    components: {
      DependenciesManagerTargetsCol,
      PlanningElementView,
      SelectElement,
    },
    props: {
      planning: { type: Object, required: true },
      element: { type: Object, required: true },
    },
    emits: ['close'],
    data() {
      return {
        el: this.element,
        showAddDependency: false,
        newTarget: null,
        isSuccessor: false,
        newTargetType: null,
        newTargetDelay: 0,
        dependencyTypeItems: [
          { title: this.$t('DEPENDENCIES.FS_DESC'), value: null },
          { title: this.$t('DEPENDENCIES.SS_DESC'), value: 'SS' },
          { title: this.$t('DEPENDENCIES.FF_DESC'), value: 'FF' },
        ],
        dependenciesStore: useDependenciesStore(),
        usersAccessRightStore: useUsersAccessRightStore(),
        planningElementsStore: usePlanningElementsStore(),
      };
    },
    computed: {
      displayedEl() {
        return this.displayableEl(this.el);
      },
      validDependencies() {
        const validDependencies = { predecessors: [], successors: [] };
        (this.el.getDependencies() || []).forEach((dependency) => {
          const targetEl = this.planning.elements.find(el => el.id == dependency.target_id);
          if (! targetEl) {
            this.deleteDependency(this.el, { id: dependency.target_id }, dependency.successor);
          } else if (dependency.successor) {
            validDependencies.successors.push({ ...dependency, targetEl });
          } else {
            validDependencies.predecessors.push({ ...dependency, targetEl });
          }
        });
        validDependencies.predecessors.sort((a, b) => b.targetEl.getEndTime().diff(a.targetEl.getEndTime()));
        validDependencies.successors.sort((a, b) => a.targetEl.getStartTime().diff(b.targetEl.getStartTime()));
        return validDependencies;
      },
      newTargetIsAlreadyDependency() {
        if (! this.newTarget || typeof this.newTarget !== 'object') return false;
        return (this.el.getDependencies() || []).findIndex(dependency => dependency.target_id == this.newTarget.id) > -1;
      },
      otherElements() {
        return this.planning.elements.filter((item) => {
          return item.id != this.el.id && item.hasDates() && ! item.isType('macro');
        });
      },
      predecessorCandidates() {
        const { sourceAnchor, targetAnchor } = getDependencyAnchors(this.newTargetType, false);
        const sourceTime = getAnchorTime(this.el, sourceAnchor);
        return this.otherElements.filter((item) => {
          return getAnchorTime(item, targetAnchor).isBefore(sourceTime);
        }).sort((a, b) => b.getEndTime().diff(a.getEndTime()));
      },
      successorCandidates() {
        const { sourceAnchor, targetAnchor } = getDependencyAnchors(this.newTargetType, true);
        const sourceTime = getAnchorTime(this.el, sourceAnchor);
        return this.otherElements.filter((item) => {
          return getAnchorTime(item, targetAnchor).isAfter(sourceTime);
        }).sort((a, b) => a.getStartTime().diff(b.getStartTime()));
      },
      dependencyCandidates() {
        return this.isSuccessor ? this.successorCandidates : this.predecessorCandidates;
      },
      canEditDependencyType() {
        return this.usersAccessRightStore.config.dependencyTypes;
      },
      elHasDependencies() {
        return !! this.el.getDependencies()?.length;
      },
    },
    methods: {
      displayableEl(el) {
        const displayableEl = angular.copy(el);
        displayableEl.ytextposition = 20;
        displayableEl.setConfig({ 'show-progress': false }); // hide all
        if (displayableEl.getIsLocked()) displayableEl.setConfig({ 'show-dates': true, 'show-date': true, 'show-progress': false });
        return displayableEl;
      },
      deleteDependency(fromEl, targetEl, isSuccessor) {
        const dependencies = fromEl.getDependencies()?.slice() || [];
        const index = dependencies.findIndex(dependency => dependency.target_id == targetEl.id && ! dependency.successor == ! isSuccessor);
        if (index > -1) {
          dependencies.splice(index, 1);
          fromEl.setDependencies(dependencies);
        }
      },
      deletePredecessor(targetEl) {
        this.deleteDependency(this.el, targetEl, false);
        this.deleteDependency(targetEl, this.el, true);
      },
      deleteSuccessor(targetEl) {
        this.deleteDependency(this.el, targetEl, true);
        this.deleteDependency(targetEl, this.el, false);
      },
      addDependency() {
        if (! this.newTarget || typeof this.newTarget !== 'object' || this.newTarget.id == this.el.id) return;
        this.dependenciesStore.addDependency(this.el, this.newTarget, this.isSuccessor, this.newTargetDelay, this.newTargetType);
        this.newTarget = null;
      },
      updateDependency({ successor, delay, type, targetEl }) {
        this.dependenciesStore.addDependency(this.el, targetEl, successor, delay || 0, type || null);
      },
      setMainEl(newel) {
        this.el = newel;
      },
      reduceElDependenciesToMinimum(direction = 'successors') {
        const relatedEls = this.planning.elements.filter(item => item.getDependencies()?.length && item.hasDates()).map(el => angular.copy(el));
        const relatedEl = relatedEls.find(item => item.id === this.el.id);
        if (! relatedEl) return;
        this.$emit('close');

        const reducedElements = direction == 'predecessors'
          ? DependenciesSrv.calculateElPredecessors(new Set(), relatedEls, relatedEl)
          : DependenciesSrv.calculateElSuccessors(new Set(), relatedEls, relatedEl);

        const { hasChanges, msgbox } = openReduceElDependenciesDialog(reducedElements, direction);
        if (! hasChanges) return;

        msgbox.then(() => { // apply modifications
          this.planningElementsStore.startChangingElement();
          reducedElements.forEach((el) => {
            const realEl = this.planningElementsStore.getElement(el.id);
            realEl.setStartTime(el.getStartTime());
            realEl.setEndTime(el.getEndTime());
            realEl.update();
          });
          this.planningElementsStore.changingElement();
        }).catch(() => {});
      },
    },
  };
</script>
